import React, { useState } from "react";
import { Fields } from "./Fields";
import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Switch,
  TextField,
  useTheme,
} from "@mui/material";
import ErrorsList from "../../ui/ErrorsList";
import { createYupSchema } from "../../utilities/yupSchemaCreator";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useNavigate } from "react-router-dom";
import { CalUtcWithFormat, newValueConvert } from "../../utilities/functions";
import { useWhatsAppNumber } from "./provider";
import dayjs from "dayjs";
import "dayjs/locale/de";
import "dayjs/locale/en-gb";

const yupSchema = Fields.reduce(createYupSchema, {});
const formSchema = Yup.object().shape(yupSchema);

const FormWhatsAppNumber = ({ handleSuccess, whatsAppNumber }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { register, handleSubmit, setValue, formState } = useForm({
    resolver: yupResolver(formSchema),
  });
  const [error, setError] = useState();
  const [locale] = useState("en-gb");

  const { create, edit } = useWhatsAppNumber();

  const onSubmit = async (values) => {
    values.initialDate
      ? (values.initialDate = CalUtcWithFormat(values.initialDate))
      : (values.initialDate = whatsAppNumber?.initialDate);
    values.expiryDate
      ? (values.expiryDate = CalUtcWithFormat(values.expiryDate))
      : (values.expiryDate = whatsAppNumber?.expiryDate);
    try {
      if (whatsAppNumber) await edit(whatsAppNumber?.id, values);
      else await create(values);
      handleSuccess();
    } catch (error) {
      console.error(error.errors.message);
      setError({ title: error.errors.message });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      {error && (
        <Alert severity="error" sx={{ m: 3 }}>
          <ErrorsList error={error} />
        </Alert>
      )}
      <Card
        sx={{
          p: theme.dimensions.padding,
          mt: theme.dimensions.mt,
          mb: theme.dimensions.mb,
        }}
      >
        <Grid container spacing={2} columns={{ xs: 2, sm: 2, md: 2 }}>
          {Fields.map((f, index) => (
            <Grid item xs={1} key={index}>
              {f.date ? (
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale={locale}
                >
                  <FormControl
                    components={["DateRangePicker"]}
                    sx={{
                      width: theme.dimensions.width,
                      mt: theme.dimensions.mt,
                      mb: theme.dimensions.mb,
                    }}
                  >
                    <span style={{ fontSize: "12px" }}>{f.label}</span>
                    <DateTimePicker
                      sx={{ mt: 1 }}
                      error={!!formState?.errors[f.name]}
                      onChange={(date) => setValue(f.name, date)}
                      defaultValue={dayjs(
                        newValueConvert(whatsAppNumber?.[f.name])
                      )}
                      slotProps={{
                        textField: {
                          error: formState?.errors[f.name]?.message,
                          helperText: (
                            <p style={{ color: "#d32f2f" }}>
                              {formState?.errors[f.name]?.message}
                            </p>
                          ),
                          size: "small",
                        },
                      }}
                    />
                  </FormControl>
                </LocalizationProvider>
              ) : (
                <>
                  {f.switch ? (
                    <FormGroup>
                      <FormLabel
                        component="legend"
                        sx={{
                          mt: theme.dimensions.mt,
                        }}
                      >
                        {f.label}
                      </FormLabel>
                      <FormControlLabel
                        sx={{
                          mt: theme.dimensions.mt,
                        }}
                        control={
                          <Switch
                            color="success"
                            defaultChecked={
                              whatsAppNumber ? whatsAppNumber?.isActive : true
                            }
                          />
                        }
                        {...register(f?.name)}
                        label="Active"
                      />
                    </FormGroup>
                  ) : (
                    <>
                      {f.forUI ? (
                        <Box></Box>
                      ) : (
                        <>
                          {whatsAppNumber &&
                          (f.name === "phoneNumberId" ||
                            f.name === "number") ? (
                            <Box></Box>
                          ) : (
                            <FormControl>
                              <span style={{ fontSize: "12px" }}>
                                {f.label}
                              </span>
                              <TextField
                                error={
                                  !!formState?.errors[f.name] ||
                                  (f.required &&
                                    !formState.dirtyFields?.[f.name] &&
                                    !whatsAppNumber)
                                }
                                multiline={f.textarea}
                                rows={4}
                                defaultValue={whatsAppNumber?.[f.name]}
                                helperText={
                                  formState?.errors[f.name]?.message ||
                                  (f.required &&
                                  !formState.dirtyFields?.[f.name] &&
                                  !whatsAppNumber
                                    ? "This field is required"
                                    : "")
                                }
                                sx={{
                                  width: theme.dimensions.width,
                                  mt: theme.dimensions.mt,
                                  mb: theme.dimensions.mb,
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor:
                                        f.required &&
                                        !formState.dirtyFields?.[f.name] &&
                                        !whatsAppNumber
                                          ? "red"
                                          : "",
                                    },
                                  },
                                }}
                                inputProps={{
                                  ...register(f.name, {
                                    required:
                                      f.required && !whatsAppNumber
                                        ? "This field is required"
                                        : false,
                                  }),
                                }}
                                required={f.required && !whatsAppNumber}
                                type={f.type}
                                size="small"
                              />
                            </FormControl>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </Grid>
          ))}
        </Grid>
      </Card>
      <Box sx={{ display: "flex", justifyContent: "end" }}>
        <Button
          variant="contained"
          onClick={() => navigate(-1)}
          sx={{
            backgroundColor: theme.color.grayBackground,
            color: theme.color.dark,
            "&:hover": { background: `${theme.color.gray50}` },
          }}
        >
          Discard
        </Button>
        <Button
          variant="contained"
          type="submit"
          sx={{
            backgroundColor: theme.color.blackBackground,
            ml: theme.dimensions.ml,
            "&:hover": { background: `${theme.color.black}` },
            mx: 2,
          }}
        >
          {whatsAppNumber ? <>Update</> : <>Add</>} WhatsApp Account
        </Button>
      </Box>
    </form>
  );
};

export default FormWhatsAppNumber;
